import PropTypes from 'prop-types';
import React, { useCallback, useState, useRef } from 'react';
import { useSelector } from 'react-redux';

import { useTransferCustomization } from '@wetransfer/transfer/component/TransferCustomizationProvider/hooks';
import Helper from '@wetransfer/transfer/helper';
import Analytics from '@wetransfer/transfer/modules/analytics';

/* Redux */
import { accountSelector } from '@wetransfer/transfer/store/selector/account';
import { channelSelector } from '@wetransfer/transfer/store/selector/channel';
import { selectIsConsentCookieOverlayActive } from '@wetransfer/transfer/store/selector/consent';
import { selectTransfer } from '@wetransfer/transfer/store/selector/transfer';
import { selectUiIsOptionsVisible } from '@wetransfer/transfer/store/selector/ui';
import { selectUserIsLoggedIn } from '@wetransfer/transfer/store/selector/user';
import { getIsForwardedTransfer } from '@wetransfer/transfer/transfer-bundle/store/selectors';

import UploaderForm from './UploaderForm';
import {
  useCreateTransfer,
  useIsDisabled,
  useScrollable,
  useUploaderFormDispatchers,
  useUploadTransfer,
  useValidateTransfer,
} from './UploaderForm.hooks';

const UploaderFormContainer = ({ transferWindowRef }) => {
  const {
    clearTransferNotification,
    pushTransferNotification,
    changeTransferType,
    setIsOptionsVisible,
  } = useUploaderFormDispatchers();
  const account = useSelector(accountSelector);
  const channel = useSelector(channelSelector);
  const transfer = useSelector(selectTransfer);
  const isCookieOverlayActive = useSelector(selectIsConsentCookieOverlayActive);
  const isUserLoggedIn = useSelector(selectUserIsLoggedIn);
  const isForwardedTransfer = useSelector(getIsForwardedTransfer);
  const isOptionsVisible = useSelector(selectUiIsOptionsVisible);

  const isRecipientLocked = Helper.isDefaultRecipientInUploadLocked({
    channelDefaultRecipient: channel.defaultRecipient,
    userLoggedIn: isUserLoggedIn,
    channelName: channel.name,
    accountSubDomainName: account.subdomain_name,
  });

  const isTooltip =
    !isRecipientLocked &&
    !isUserLoggedIn &&
    transfer.showTransferOptionsTooltip;

  const [transferInitialized, setTransferInitialized] = useState(false);
  const [isDisabled, setIsDisabled] = useIsDisabled();

  const formRef = useRef(null);
  const optionsRef = useRef(null);
  const { scrollableRef, scrollTo, getScrollPosition } = useScrollable();

  const trackClickMoreButton = useCallback(() => {
    Analytics.trackSnowplowEvent({
      category: 'transfer',
      action: 'click_more_button',
    });
    Analytics.trackAmplitude({
      action: 'Transfer Options Clicked',
    });
  }, []);

  useCreateTransfer();
  useValidateTransfer(setIsDisabled);
  useUploadTransfer(transferInitialized);

  const { closeDrawer } = useTransferCustomization();

  function hideOptions() {
    closeDrawer();

    if (!isOptionsVisible || !optionsRef.current) {
      return;
    }

    if (optionsRef.current) {
      const newTop = getScrollPosition() - optionsRef.current.node.offsetHeight;
      scrollTo(newTop, {
        animated: true,
        duration: 300,
        onScrollingEnd: () => {
          setIsOptionsVisible(false);
        },
      });
    }
  }

  function hideOptionsOnScroll(e) {
    if (!isOptionsVisible) {
      return;
    }

    if (!optionsRef.current) {
      return;
    }

    const optionsRefNode = optionsRef.current.node;

    if (
      optionsRefNode &&
      e.scrollTop + e.clientHeight <= optionsRefNode.offsetTop + 30
    ) {
      clearTransferNotification();
      hideOptions();
    }
  }

  function handleLockedRecipientClick() {
    pushTransferNotification({
      text: 'LockedRecipient',
      align: '.uploader__locked-recipient',
    });
  }

  function toggleOptions() {
    if (!isOptionsVisible) {
      trackClickMoreButton();
      setIsOptionsVisible(true);
    }

    if (isOptionsVisible) {
      hideOptions();
    }
  }

  function startTransfer() {
    if (isDisabled) {
      return;
    }

    setTransferInitialized(true);
  }

  const uploaderFormProps = {
    channel,
    transfer,
    isCookieOverlayActive,
    isDisabled,
    isForwardedTransfer,
    isOptionsVisible,
    isRecipientLocked,
    isTooltip,
    changeTransferType,
    optionsRef,
    scrollableRef,
    hideOptionsOnScroll,
    hideOptions,
    handleLockedRecipientClick,
    toggleOptions,
    startTransfer,
    transferWindowRef,
  };

  return <UploaderForm {...uploaderFormProps} ref={formRef} />;
};

UploaderFormContainer.propTypes = {
  transferWindowRef: PropTypes.object,
};

export default UploaderFormContainer;
